import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "./Logo";
import ThemeToggle from "./ThemeToggle";
import { Menu, X, ArrowRight } from "lucide-react";
import { brand } from "../mock";

const navItems = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Insights", to: "/insights" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onHome = pathname === "/";
  const onScroll = useCallback(() => setScrolled(window.scrollY > 40), []);

  useEffect(() => {
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  const forceLight = !onHome || scrolled;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        forceLight
          ? "bg-white/95 dark:bg-[#0B1B3B]/95 backdrop-blur shadow-[0_2px_20px_-10px_rgba(11,27,59,0.15)]"
          : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-[72px]">
        <Logo variant={forceLight ? "light" : "dark"} />

        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((n) => {
            const active = pathname === n.to || (n.to === "/insights" && (pathname.startsWith("/case-studies") || pathname.startsWith("/blog")));
            return (
              <Link
                key={n.to}
                to={n.to}
                className={`text-sm font-medium transition-colors hover:text-[#1E56E3] relative ${
                  active ? "text-[#1E56E3]" : forceLight ? "text-[#0B1B3B] dark:text-white" : "text-white"
                }`}
              >
                {n.label}
                {active && <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-[#1E56E3] rounded-full" />}
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <ThemeToggle inverted={!forceLight} />
          <Link
            to={brand.bookingPath}
            className="inline-flex items-center gap-2 bg-[#1E56E3] hover:bg-[#1746BF] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors"
            data-testid="header-book-appointment-button"
          >
            Book appointment <ArrowRight size={16} />
          </Link>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <ThemeToggle inverted={!forceLight} />
          <button onClick={() => setOpen(!open)} className={`p-2 rounded ${forceLight ? "text-[#0B1B3B] dark:text-white" : "text-white"}`} aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white dark:bg-[#0B1B3B] border-t border-slate-200 dark:border-white/10 shadow-lg">
          <div className="container-x py-4 flex flex-col gap-2">
            {navItems.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-sm font-medium text-[#0B1B3B] dark:text-white py-2">{n.label}</Link>
            ))}
            <Link
              to={brand.bookingPath}
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#1E56E3] hover:bg-[#1746BF] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors"
              data-testid="mobile-book-appointment-button"
            >
              Book appointment <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
